import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import type { TourStop } from 'fym-shared/src/music24/onboardingTour';
import { TypedMemo } from '../../common/types';
import {
  placeTourCard,
  TourCardPlacement,
  TourSize,
  tourHoleOf,
  tourScrollBlock,
  tourScrollDelta,
} from './tourCardPlacement';
import { scrollParentOf, useTourTarget } from './useTourTarget';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the tour; each part uses its own slice of it */
import styles from './TourGuideBar.module.scss';
/* eslint-enable css-modules/no-unused-class */

export interface TourSpotlightProps {
  stop: TourStop;
  /** The id of the card title, for `aria-labelledby`. */
  titleId: string;
  onExit: () => void;
  /** The card content: counter, message, dots and actions. */
  children: React.ReactNode;
}

const FOCUSABLE = 'button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])';

const PLACEMENT_CLASS: Record<TourCardPlacement, string | undefined> = {
  below: styles.cardNext,
  above: styles.cardNext,
  right: styles.cardNext,
  left: styles.cardNext,
  over: styles.cardNext,
  center: styles.cardCenter,
  sheet: styles.cardSheet,
};

function viewportSize(): TourSize {
  return { width: window.innerWidth, height: window.innerHeight };
}

function prefersReducedMotion(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function stopEvent(event: React.SyntheticEvent): void {
  event.preventDefault();
  event.stopPropagation();
}

/**
 * The spotlight of one tour stop (owner request 2026-10-09; no Claude Design frame: built with the
 * Music24 tokens). A dim backdrop covers the whole page, the sidebar too. The page part of the stop
 * (`data-tour="<stop>"`) stays bright in a cut-out (a box-shadow spotlight) that follows it on
 * scroll and resize. The card sits next to the cut-out (below, above or beside it, never on it), as a bottom sheet under
 * 720 px, and as a centered modal when the page has no such part.
 *
 * The backdrop takes every click and sends the wheel to the scroller of the target, so the page
 * under it never gets a click. Esc exits the tour. Focus moves to the card, and Tab stays in it.
 */
const TourSpotlightComponent: React.FC<TourSpotlightProps> = ({ stop, titleId, onExit, children }) => {
  const { element, box } = useTourTarget(stop);
  const cardRef = useRef<HTMLDivElement>(null);
  const [viewport, setViewport] = useState<TourSize>(viewportSize);
  const [cardSize, setCardSize] = useState<TourSize>({ width: 0, height: 0 });
  const scrolledStop = useRef<TourStop | null>(null);

  useEffect(() => {
    const onResize = (): void => setViewport(viewportSize());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // The card size decides below or above. Measure it when the stop, the target or the window changes.
  useLayoutEffect(() => {
    const card = cardRef.current;
    if (card === null) {
      return;
    }
    const { offsetWidth: width, offsetHeight: height } = card;
    setCardSize((current) => (current.width === width && current.height === height ? current : { width, height }));
  }, [stop, element, viewport]);

  // One scroll per stop: the target comes on screen when the stop opens, then the user may scroll.
  useEffect(() => {
    if (element === null || scrolledStop.current === stop || typeof element.scrollIntoView !== 'function') {
      return;
    }
    scrolledStop.current = stop;
    const rect = element.getBoundingClientRect();
    const card = { width: cardRef.current?.offsetWidth ?? 0, height: cardRef.current?.offsetHeight ?? 0 };
    const behavior: ScrollBehavior = prefersReducedMotion() ? 'auto' : 'smooth';
    // Wide screens: scroll so the card fits under or over the spotlight and never covers it.
    const delta = tourScrollDelta(
      tourHoleOf({ top: rect.top, left: rect.left, width: rect.width, height: rect.height }),
      card,
      viewportSize()
    );
    const scroller = scrollParentOf(element);
    if (delta !== null && scroller !== null && typeof scroller.scrollBy === 'function') {
      if (delta !== 0) {
        scroller.scrollBy({ top: delta, behavior });
      }
      return;
    }
    element.scrollIntoView({ block: tourScrollBlock(rect.height, viewportSize(), card.height), behavior });
  }, [element, stop]);

  // Focus moves to the card at each stop; it goes back to where it was when the tour ends.
  useEffect(() => {
    cardRef.current?.focus({ preventScroll: true });
  }, [stop]);
  useEffect(() => {
    const before = document.activeElement;
    return () => {
      if (before instanceof HTMLElement && before.isConnected) {
        before.focus({ preventScroll: true });
      }
    };
  }, []);

  // Esc exits. Tab and Shift+Tab stay in the card, also when the focus was on the page.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onExit();
        return;
      }
      const card = cardRef.current;
      if (event.key !== 'Tab' || card === null) {
        return;
      }
      const focusable = Array.from(card.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusable.length === 0) {
        event.preventDefault();
        card.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (first === undefined || last === undefined) {
        return;
      }
      const current = document.activeElement;
      const inside = current instanceof Node && card.contains(current);
      if (!inside || (event.shiftKey && (current === first || current === card))) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (!event.shiftKey && current === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [onExit]);

  const onWheel = useCallback(
    (event: React.WheelEvent) => {
      scrollParentOf(element)?.scrollBy({ top: event.deltaY, left: event.deltaX });
    },
    [element]
  );

  const hole = useMemo(() => (box === null ? null : tourHoleOf(box)), [box]);
  const position = placeTourCard(hole, cardSize, viewport);
  const cardStyle: React.CSSProperties | undefined =
    position.top === undefined ? undefined : { top: position.top, left: position.left };

  return createPortal(
    // The layer stops the React events too: the portal sits inside the page tree.
    <div
      className={styles.layer}
      role="presentation"
      data-testid="tour-spotlight"
      onClick={(event) => event.stopPropagation()}
    >
      <div
        className={clsx(styles.backdrop, hole === null && styles.backdropDim)}
        role="presentation"
        data-testid="tour-backdrop"
        aria-hidden="true"
        onMouseDown={stopEvent}
        onClick={stopEvent}
        onWheel={onWheel}
      />
      {hole !== null && (
        <div
          className={styles.hole}
          data-testid="tour-hole"
          aria-hidden="true"
          style={{ top: hole.top, left: hole.left, width: hole.width, height: hole.height }}
        />
      )}
      <div
        ref={cardRef}
        className={clsx(styles.card, PLACEMENT_CLASS[position.placement])}
        style={cardStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        data-testid="tour-guide-bar"
        data-placement={position.placement}
      >
        {children}
      </div>
    </div>,
    document.body
  );
};

export const TourSpotlight = TypedMemo(TourSpotlightComponent);
